'use client';

import React, { createContext, useContext, useState, useEffect } from 'react';
import en from '@/locales/en.json';
import es from '@/locales/es.json';
import fr from '@/locales/fr.json';
import de from '@/locales/de.json';
import it from '@/locales/it.json';
import ptBR from '@/locales/pt-BR.json';
import nl from '@/locales/nl.json';
import ar from '@/locales/ar.json';
import zh from '@/locales/zh.json';
import jp from '@/locales/jp.json';
import ko from '@/locales/ko.json';

type Translations = typeof en;
type TranslationKey = string;

const localeMap: Record<string, Translations> = {
  'en': en,
  'es': es,
  'fr': fr,
  'de': de,
  'it': it,
  'pt-BR': ptBR,
  'nl': nl,
  'ar': ar,
  'zh': zh,
  'jp': jp,
  'ko': ko,
};

interface I18nContextType {
  locale: string;
  translations: Translations;
  t: (key: string, params?: Record<string, string | number>) => string;
  setLocale: (newLocale: string) => void;
}

const I18nContext = createContext<I18nContextType | undefined>(undefined);

interface I18nProviderProps {
  children: React.ReactNode;
  locale?: string;
}

export function I18nProvider({ children, locale: initialLocale = 'en' }: I18nProviderProps) {
  const [locale, setLocale] = useState<string>(initialLocale);
  const [translations, setTranslations] = useState<Translations>(en);
  
  // Update locale when initialLocale prop changes
  useEffect(() => {
    setLocale(initialLocale);
  }, [initialLocale]);

  // Helper function to get nested value from object using dot notation
  const getNestedValue = (obj: any, path: string): string => {
    return path.split('.').reduce((current, key) => current?.[key], obj) || path;
  };

  // Translation function with parameter replacement
  const t = (key: string, params?: Record<string, string | number>): string => {
    let translation = getNestedValue(translations, key);
    
    if (typeof translation !== 'string') {
      console.warn(`Translation key "${key}" not found`);
      return key;
    }

    // Replace parameters in the translation
    if (params) {
      Object.entries(params).forEach(([paramKey, value]) => {
        translation = translation.replace(new RegExp(`{{${paramKey}}}`, 'g'), String(value));
      });
    }

    return translation;
  };

  // Load translations based on locale
  useEffect(() => {
    const loadedTranslations = localeMap[locale] || en;
    setTranslations(loadedTranslations);
  }, [locale]);

  return (
    <I18nContext.Provider value={{ locale, translations, t, setLocale }}>
      {children}
    </I18nContext.Provider>
  );
}

export function useTranslation() {
  const context = useContext(I18nContext);
  if (!context) {
    throw new Error('useTranslation must be used within an I18nProvider');
  }
  return context;
}